import React, { createContext, useState, useCallback } from "react";
import { usersService } from "@/services/usersService";

export const PermissionContext = createContext();

export const PermissionProvider = ({ children }) => {
  const [userPermissions, setUserPermissions] = useState([]);
  const [userRoles, setUserRoles] = useState([]);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [currentUserEmail, setCurrentUserEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);

  // Check if user has access to a page
  const hasPageAccess = useCallback((pageName) => {
    // Permissions must be loaded before granting access
    if (!permissionsLoaded) {
      console.log("Permissions still loading, denying access to:", pageName);
      return false;
    }

    // If user has NO roles assigned, DENY all access
    if (!userRoles || userRoles.length === 0) {
      console.log("No roles assigned to user, denying access to:", pageName);
      return false;
    }

    // Check if user has specific permission for this page
    const hasAccess = userPermissions.some((perm) => perm.page_name === pageName);
    console.log(`Checking access for ${pageName}:`, hasAccess, "Permissions count:", userPermissions.length, "Roles:", userRoles.length);
    return hasAccess;
  }, [userPermissions, userRoles, permissionsLoaded]);

  // Get all accessible pages
  const getAccessiblePages = useCallback(() => {
    return userPermissions;
  }, [userPermissions]);

  // Fetch current user profile from token (verifies email against database)
  const fetchCurrentUserProfile = useCallback(async () => {
    try {
      setLoading(true);
      setPermissionsLoaded(false);
      console.log("PermissionContext: Verifying current user from token");
      
      // Use verifyProfile which extracts email from token and checks database
      const response = await usersService.verifyProfile();
      const userData = response.data;
      
      console.log("PermissionContext: User profile verified successfully:", {
        email: userData.email,
        in_database: userData.in_database,
        roles: userData.roles,
        pages: userData.pages,
        pageCount: userData.pages?.length || 0
      });
      
      setCurrentUserId(userData.user_id || userData.email);
      setCurrentUserEmail(userData.email || '');
      setUserRoles(userData.roles || []);
      // Backend returns pages array with page_name and page_path
      setUserPermissions(userData.pages || []);
      setPermissionsLoaded(true);
      console.log("PermissionContext: permissionsLoaded set to TRUE");
    } catch (error) {
      console.error("PermissionContext: Error verifying user profile:", error);
      setUserPermissions([]);
      setUserRoles([]);
      setCurrentUserEmail('');
      setPermissionsLoaded(true);
      console.log("PermissionContext: permissionsLoaded set to TRUE (after error)");
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch user permissions from backend by ID
  const fetchUserPermissions = useCallback(async (userId) => {
    try {
      setLoading(true);
      setPermissionsLoaded(false);
      console.log("PermissionContext: Starting permission fetch for user_id:", userId);
      
      // Use usersService which includes auth headers via apiClient
      const response = await usersService.get(userId);
      const userData = response.data;
      
      console.log("PermissionContext: User permissions fetched successfully:", {
        userId,
        roles: userData.roles,
        pages: userData.pages,
        pageCount: userData.pages?.length || 0
      });
      
      setCurrentUserId(userId);
      setUserRoles(userData.roles || []);
      // Backend returns pages array with page_name and page_path
      setUserPermissions(userData.pages || []);
      setPermissionsLoaded(true);
      console.log("PermissionContext: permissionsLoaded set to TRUE");
    } catch (error) {
      console.error("PermissionContext: Error fetching user permissions:", error);
      setUserPermissions([]);
      setUserRoles([]);
      setPermissionsLoaded(true);
      console.log("PermissionContext: permissionsLoaded set to TRUE (after error)");
    } finally {
      setLoading(false);
    }
  }, []);

  return (
    <PermissionContext.Provider
      value={{
        userPermissions,
        userRoles,
        currentUserId,
        currentUserEmail,
        loading,
        permissionsLoaded,
        hasPageAccess,
        getAccessiblePages,
        fetchUserPermissions,
        fetchCurrentUserProfile,
        setUserPermissions,
        setUserRoles,
        setCurrentUserId,
        setCurrentUserEmail,
        setPermissionsLoaded,
      }}
    >
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermissions = () => {
  const context = React.useContext(PermissionContext);
  if (!context) {
    throw new Error("usePermissions must be used within PermissionProvider");
  }
  return context;
};
